import React, { useState, useEffect } from "react";
import { useHistory, useParams } from 'react-router-dom';
import { Col, Row, Card, Form, Button, Alert, Spinner } from '@themesberg/react-bootstrap';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { uploadToR2 } from '../../utils/r2Upload';

export default () => {
  const history = useHistory();
  const { id } = useParams();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    age: "",
    city: "",
    description: "",
    profileImageUrl: null,
    profileImage: null
  });
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    loadMemberData();
  }, [id]);

  const loadMemberData = async () => {
    try {
      setLoadingData(true);
      const docRef = doc(db, 'users', id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        setError("Üye bulunamadı.");
        setLoadingData(false);
        return;
      }

      const data = docSnap.data();
      setFormData({
        firstName: data.firstName || data.name?.split(' ')[0] || "",
        lastName: data.lastName || data.name?.split(' ').slice(1).join(' ') || "",
        email: data.email || "",
        phone: data.phone || data.phoneNumber || "",
        age: data.age?.toString() || "",
        city: data.city || "",
        description: data.description || data.bio || "",
        profileImageUrl: data.profileImageUrl || null,
        profileImage: null
      });
    } catch (err) {
      console.error("Error loading member:", err);
      setError("Üye yüklenirken hata oluştu: " + err.message);
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError(null);
  };

  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        setError(`Profil fotoğrafı çok büyük. Maksimum 10MB olmalıdır. (Seçilen: ${(file.size / 1024 / 1024).toFixed(2)}MB)`);
        e.target.value = '';
        return;
      }
      setFormData(prev => ({ ...prev, profileImage: file }));
      if (error) setError(null);
    }
  };

  const handleRemoveProfilePhoto = () => {
    setFormData(prev => ({
      ...prev,
      profileImageUrl: null,
      profileImage: null
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      
      const docRef = doc(db, 'users', id);
      const updateData = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        name: `${formData.firstName.trim()} ${formData.lastName.trim()}`.trim(),
        email: formData.email.trim() || null,
        phone: formData.phone.trim() || null,
        phoneNumber: formData.phone.trim() || null,
        age: formData.age ? parseInt(formData.age) : null,
        city: formData.city.trim() || null,
        description: formData.description.trim() || null,
        bio: formData.description.trim() || null,
        profileImageUrl: formData.profileImageUrl
      };

      if (formData.profileImage) {
        const downloadUrl = await uploadToR2(
          formData.profileImage,
          `user_profiles/profile_${Date.now()}_${formData.profileImage.name}`
        );
        updateData.profileImageUrl = downloadUrl;
      }

      await updateDoc(docRef, updateData);
      setSuccess(true);
      setTimeout(() => {
        history.push('/members/list');
      }, 1500);
    } catch (err) {
      console.error("Error updating member:", err);
      setError("Üye güncellenirken hata oluştu: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <>
      <div className="py-4">
        <h4>Düzenle</h4>
        <p className="text-muted mb-0">Üyeler / Düzenle</p>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success">
          Üye başarıyla güncellendi. Yönlendiriliyorsunuz...
        </Alert>
      )}

      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <h5 className="mb-4">Üye Bilgileri</h5>

          <Form onSubmit={handleSubmit}>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>İsim</Form.Label>
                  <Form.Control
                    type="text"
                    name="firstName"
                    placeholder="İsim"
                    value={formData.firstName}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Soyisim</Form.Label>
                  <Form.Control
                    type="text"
                    name="lastName"
                    placeholder="Soyisim"
                    value={formData.lastName}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    placeholder="email@example.com"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Telefon</Form.Label>
                  <Form.Control
                    type="text"
                    name="phone"
                    placeholder="+90 555 123 45 67"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Yaş</Form.Label>
                  <Form.Control
                    type="number"
                    name="age"
                    placeholder="25"
                    value={formData.age}
                    onChange={handleChange}
                    min="18"
                    max="99"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Şehir</Form.Label>
                  <Form.Control
                    type="text"
                    name="city"
                    placeholder="İstanbul"
                    value={formData.city}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <h5 className="mb-4 mt-4">Profil Fotoğrafı Yönetimi</h5>
            <Row className="mb-4">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Profil Fotoğrafı</Form.Label>
                  <div className="d-flex align-items-start gap-4">
                    <div className="text-center">
                      {formData.profileImageUrl ? (
                        <div className="mb-2">
                          <img
                            src={formData.profileImageUrl}
                            alt="Profil"
                            style={{ width: '150px', height: '150px', objectFit: 'cover', borderRadius: '8px' }}
                          />
                        </div>
                      ) : (
                        <div 
                          className="mb-2 bg-dark d-flex align-items-center justify-content-center text-white"
                          style={{ width: '150px', height: '150px', borderRadius: '8px' }}
                        >
                          Sansürlü / Yok
                        </div>
                      )}
                      
                      {formData.profileImageUrl && (
                        <Button 
                          variant="danger" 
                          size="sm" 
                          onClick={handleRemoveProfilePhoto}
                          className="w-100"
                        >
                          Fotoğrafı Sansürle / Kaldır
                        </Button>
                      )}
                    </div>
                    
                    <div className="flex-grow-1">
                      <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={handleProfileImageChange}
                        className="mb-2"
                      />
                      <Form.Text className="text-muted d-block">
                        Yeni bir fotoğraf seçerek kullanıcının profil fotoğrafını güncelleyebilirsiniz.
                        Fotoğrafı kaldırmak (sansürlemek) için "Fotoğrafı Sansürle / Kaldır" butonunu kullanın.
                      </Form.Text>
                      {formData.profileImage && (
                        <div className="mt-3">
                          <p className="text-muted mb-1">Yeni Seçilen Fotoğraf (Kaydedince güncellenecek):</p>
                          <img
                            src={URL.createObjectURL(formData.profileImage)}
                            alt="Yeni profil önizleme"
                            style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px' }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-4">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Açıklama</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="description"
                    placeholder="Açıklama"
                    value={formData.description}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={() => history.push('/members/list')}>
                İptal
              </Button>
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? 'Kaydediliyor...' : 'Kaydet'}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </>
  );
};

